import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Skeleton } from '@/shared/ui/skeleton';
import { PublicInvitePage, type InviteReferral } from '@/features/public-invite';
import { claimReferral, fetchCodeInfo } from '../../api/public-mgm';
import type { MgmCodeInfo } from '../../model/types';
import { rememberReferral, safeLocalStorage, storedReferral } from './first-touch';
import { ReferralBanner } from './referral-banner';

/**
 * Monta a indicação que o convite recebe: banner, claim no blur do
 * identificador (vira "Indicada") e o código no cadastro. O claim roda uma vez
 * por identificador digitado — digitar de novo o mesmo CPF não repete.
 */
function useInviteReferral(info: MgmCodeInfo | null): InviteReferral | undefined {
  const claimed = useRef<Set<string>>(new Set());
  return useMemo(() => {
    if (!info || info.status !== 'ok' || !info.code || !info.idPassbook) return undefined;
    const { code, idPassbook } = info;
    return {
      code,
      banner: <ReferralBanner referrerFirstName={info.referrerFirstName} rules={info.rules} />,
      onIdentify: (uniqueId: string) => {
        const key = uniqueId.replace(/\W/g, '');
        if (!key || claimed.current.has(key)) return;
        claimed.current.add(key);
        void claimReferral({ idPassbook, code, uniqueId });
      },
    };
  }, [info]);
}

/**
 * `/invite/r/:code` — o link que o participante compartilha. Resolve o código
 * para o passbook e abre o cadastro normal da loja com o banner do convite.
 * Código inválido ou campanha encerrada: o cadastro abre normal, sem banner
 * (o link nunca "morre" na mão de quem recebeu).
 */
export function MgmInviteByCodePage() {
  const { code = '' } = useParams<{ code: string }>();
  const [info, setInfo] = useState<MgmCodeInfo | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchCodeInfo(code)
      .then((data) => {
        if (cancelled) return;
        setInfo(data);
        if (data.status === 'ok' && data.idPassbook && data.code) {
          rememberReferral(safeLocalStorage(), data.idPassbook, data.code);
        }
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  const referral = useInviteReferral(info);

  if (!info && !failed) {
    return (
      <div className="container mx-auto max-w-2xl flex-1 px-4 py-10">
        <div className="space-y-6">
          <Skeleton className="mx-auto h-16 w-48" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  // Sem passbook (código inexistente) o PublicInvitePage mostra "link inválido".
  return <PublicInvitePage idPassbookOverride={info?.idPassbook ?? ''} referral={referral} />;
}

/**
 * `/invite/:idPassbook` — cadastro normal. Se este navegador abriu antes um
 * link de indicação desta loja (últimos 30 dias), a indicação continua valendo.
 * O formulário aparece na hora; o banner entra quando o código é confirmado.
 */
export function MgmAwareInvitePage() {
  const { idPassbook = '' } = useParams<{ idPassbook: string }>();
  const [info, setInfo] = useState<MgmCodeInfo | null>(null);

  useEffect(() => {
    const code = storedReferral(safeLocalStorage(), idPassbook);
    if (!code) return;
    let cancelled = false;
    fetchCodeInfo(code)
      .then((data) => {
        if (!cancelled && data.idPassbook === idPassbook) setInfo(data);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [idPassbook]);

  const referral = useInviteReferral(info);
  return <PublicInvitePage referral={referral} />;
}
